/**
 * Casino Lobby Access Control
 *
 * Controls whether users can access the in-app casino lobby or should be
 * redirected to the external PokerSolutions casino.
 */

// Whether the in-app casino lobby is enabled at all
const ALLOW_CASINO_LOBBY_ACCESS = import.meta.env.VITE_ALLOW_CASINO_LOBBY_ACCESS === 'true';

// Comma-separated list of usernames allowed to access the casino lobby
// Empty string means all users are allowed (when ALLOW_CASINO_LOBBY_ACCESS is true)
const ALLOWED_USERNAMES_RAW = import.meta.env.VITE_USERNAMES_ALLOWED_CASINO_LOBBY || '';

// Parse the comma-separated list into an array of lowercase usernames
// Also strips quotes in case env value is formatted like: "user1","user2"
const ALLOWED_USERNAMES: string[] = ALLOWED_USERNAMES_RAW
    .split(',')
    .map((name: string) => name.trim().replace(/^["']|["']$/g, '').toLowerCase())
    .filter((name: string) => name.length > 0);

/**
 * Check if a user is allowed to access the in-app casino lobby.
 *
 * Rules:
 * - If VITE_ALLOW_CASINO_LOBBY_ACCESS is false: No one can access (redirect to external)
 * - If VITE_ALLOW_CASINO_LOBBY_ACCESS is true:
 *   - If VITE_USERNAMES_ALLOWED_CASINO_LOBBY is empty: All users can access
 *   - If VITE_USERNAMES_ALLOWED_CASINO_LOBBY has values: Only listed usernames can access
 *
 * @param username - The username to check
 * @returns true if the user can access the in-app casino lobby, false if they should be redirected
 */
export const canAccessCasinoLobby = (username?: string): boolean => {
    // If casino lobby access is disabled globally, no one can access
    if (!ALLOW_CASINO_LOBBY_ACCESS) {
        return false;
    }

    // If the allowed list is empty, all users can access
    if (ALLOWED_USERNAMES.length === 0) {
        return true;
    }

    // Check if the username is in the allowed list (case-insensitive)
    if (!username) {
        return false;
    }

    return ALLOWED_USERNAMES.includes(username.toLowerCase());
};

/**
 * Check if the casino lobby feature is enabled (regardless of user-specific access)
 */
export const isCasinoLobbyEnabled = (): boolean => {
    return ALLOW_CASINO_LOBBY_ACCESS;
};

/**
 * Get the list of allowed usernames (for debugging purposes)
 */
export const getAllowedCasinoUsernames = (): string[] => {
    return [...ALLOWED_USERNAMES];
};
